import { useEffect, useRef, useState } from 'react';
import { produce } from 'immer';

import { useModal } from '../ui/ModalProvider';

import { useGetContentDetailCodeList } from './useGetContentDetailCodeList';
import { useToggle } from './useToggle';

/**
 * @description 모바일 템플릿에 있는 상세 항목들 관리
 * - 유저 입력 UI 이벤트 바인딩
 * - 정적 템플릿 틀에 맞춰 구현
 * - rule 1. 상세 항목 코드 값이 부여된 wrapping element의 dataset 값들을 통해 상태 관리
 * - rule 2. 최대한 wrapping element의 dataset 값을 css에서도 사용하여 스타일 처리
 */
export const useBindEvent = () => {
  const contentDetailCodeList = useGetContentDetailCodeList();
  const { toggles, setToggles, setAllToggle, handleToggle } = useToggle();
  const { setSignModal, guideModal, setGuideModal, resetGuideModal } = useModal();

  const bindEvent = () => {
    // 동의서 레벨 동의
    const signAll = document.querySelector('[name="sign-all"]');
    if (signAll) {
      signAll.querySelector('.btn-sign').addEventListener('click', (e) => {
        setSignModal({ open: true, code: 'sign-all' });
      });
      signAll.querySelector('.btn-clear').addEventListener('click', () => {
        signAll.dataset.error = 'Y';
        signAll.dataset.status = 'N';
        // sign은 이미지이기 때문에 data-value에 값을 저장하지 않음
      });
    }

    contentDetailCodeList.forEach((item, idx) => {
      const baseElement = document.querySelector(`[name="${item}"]`);

      // dataset type을 통해 UI 별 고정적 기능 구현
      switch (baseElement?.dataset?.type) {
        case 'toggle':
          baseElement.querySelectorAll('button').forEach((child) => {
            child.addEventListener('click', (e) => {
              const clickedButton = e.currentTarget;
              // disagreeNotAllowed => Y 고를 수 없는 병원.
              if (clickedButton.dataset.status === 'N' && baseElement.dataset.disagreeNotAllowed === 'Y') {
                setGuideModal(
                  produce(guideModal, (draft) => {
                    draft.open = true;
                    draft.title = '동의서 내용에 동의해 주세요';
                    draft.subTitle = '동의서 내용에 동의하셔야 이어서 진행할 수 있어요.';
                  }),
                );

                return;
              }
              if (clickedButton.dataset.status === 'Y' || baseElement.dataset.disagreeNotAllowed !== 'Y') {
                resetGuideModal();
              }

              const isHighlight = handleToggle(clickedButton);
              // 해당 아이템 데이터 값 저장
              if (isHighlight) {
                setToggles((cur) =>
                  produce(cur, (draft) => {
                    draft[idx] = clickedButton.dataset.status === 'Y';
                  }),
                );
              } else {
                setToggles((cur) =>
                  produce(cur, (draft) => {
                    draft[idx] = null;
                  }),
                );
              }
            });
          });
          break;

        case 'checkbox': {
          // 체크박스 내 버튼 목록
          const buttons = baseElement.querySelectorAll('button');

          // 기타 버튼
          const etcButton = baseElement.querySelector('[data-type="기타"]');

          // 기타 인풋
          const input = baseElement.querySelector('input');

          // 기타 인풋 wrapper
          const inputField = baseElement.querySelector('.input-field');

          const checkError = () => {
            const hasSelected = Array.from(buttons).some((item) => item.classList.contains('highlight'));
            const successEtc = !etcButton.classList.contains('highlight') || input.value;

            baseElement.dataset.error = hasSelected && successEtc ? 'N' : 'Y';
          };

          buttons.forEach((child) => {
            child.addEventListener('click', (e) => {
              const isToggle = e.currentTarget.classList.toggle('highlight');

              if (isToggle) {
                baseElement.dataset.value += e.currentTarget.dataset.value ? e.currentTarget.dataset.value + ',' : '';
              } else {
                baseElement.dataset.value = baseElement.dataset.value.replace(e.currentTarget.dataset.value + ',', '');
              }

              checkError();
            });
          });

          etcButton.addEventListener('click', (e) => {
            const isHighlight = e.currentTarget.classList.contains('highlight');
            inputField.style.display = isHighlight ? 'flex' : 'none';
            if (!isHighlight) {
              baseElement.dataset.value = baseElement.dataset.value.replace(input.dataset.value + ',', '');
              input.dataset.value = '';
              input.value = '';
            }
          });

          input.addEventListener('focusout', (e) => {
            const dv = baseElement.dataset.value;
            if (input.dataset.value && dv.includes(input.dataset.value + ',')) {
              baseElement.dataset.value = dv.replace(input.dataset.value, e.target.value || '');
            } else {
              baseElement.dataset.value += e.target.value ? e.target.value + ',' : '';
            }

            input.dataset.value = e.target.value;
            checkError();
          });

          // error 체크용 변수
          // const selectedGroup = new Array(buttons.length).fill(false);

          // const checkError = () => {
          //   baseElement.dataset.error =
          //     selectedGroup.some((item) => item === true) && etcErrorText.dataset.error === 'N' ? 'N' : 'Y';
          // };

          // etcInput.addEventListener('input', (e) => {
          //   if (e.target.value.length > 30) {
          //     e.target.value = e.target.value.slice(0, 30);
          //   }
          // });

          // etcInput.addEventListener('focusout', (e) => {
          //   if (etcInput.dataset.value && baseElement.dataset.value?.split(',')?.includes(etcInput.dataset.value)) {
          //     baseElement.dataset.value = baseElement.dataset.value.replace(etcInput.dataset.value + ',', '');
          //   }

          //   etcInput.dataset.value = e.target.value;
          //   baseElement.dataset.value += e.target.value ? e.target.value + ',' : '';
          //   baseElement.dataset.error = e.target.value ? 'N' : 'Y';
          //   etcErrorText.dataset.error = e.target.value ? 'N' : 'Y';
          //   selectedGroup[buttons.length - 1] = !!e.target.value;
          // });

          // buttons.forEach((child, idx) => {
          //   child.addEventListener('click', (e) => {
          //     const currentElement = e.currentTarget;
          //     const isHighlight = currentElement.classList.toggle('highlight');

          //     if (isHighlight) {
          //       // 기타 항목 케이스
          //       if (currentElement.dataset.value === '기타') {
          //         inputField.style.display = 'flex';
          //         etcErrorText.dataset.error = 'Y';
          //         baseElement.dataset.error = 'Y';
          //         selectedGroup[idx] = false;
          //         etcInput.focus();
          //       } else {
          //         baseElement.dataset.value += currentElement.dataset.value + ',';
          //         baseElement.dataset.status = currentElement.dataset.status;
          //         currentElement.dataset.error = 'N';
          //         selectedGroup[idx] = true;
          //       }
          //     } else {
          //       if (currentElement.dataset.value === '기타') {
          //         baseElement.dataset.value = baseElement.dataset.value.replace(etcInput.dataset.value + ',', '');
          //         inputField.style.display = 'none';
          //         etcInput.dataset.value = '';
          //         etcInput.value = '';
          //         etcErrorText.dataset.error = 'N';
          //       } else {
          //         baseElement.dataset.value = baseElement.dataset.value.replace(currentElement.dataset.value + ',', '');
          //         currentElement.dataset.error = 'Y';
          //         selectedGroup[idx] = false;
          //       }
          //     }
          //     checkError();
          //   });
          // });
          break;
        }
        case 'sign':
          baseElement.querySelector('.btn-sign').addEventListener('click', (e) => {
            setSignModal({ open: true, code: item });
          });

          baseElement.querySelector('.btn-clear').addEventListener('click', (e) => {
            baseElement.dataset.error = 'Y';
            baseElement.dataset.status = 'N';
            // sign은 이미지이기 때문에 data-value에 값을 저장하지 않음
          });
          break;
      }
    });
  };

  useEffect(() => {
    bindEvent();
  }, []);

  return { toggles, setToggles, setAllToggle };
};
